import React, { useState } from 'react';
import { useBrand } from '../hooks/useBrand.js';
import type { PlayerStats } from '../types/index.js';

// ============================================================
// Score decomposition — Advanced PPR logic
// ============================================================

/**
 * Scores that require the player to clarify which bag combo they threw.
 * Keys are score values, values are tuples of [bagsIn, bagsOn, bagsOff].
 */
const AMBIGUOUS_COMBOS: Record<number, Array<[number, number, number]>> = {
  3: [
    [1, 0, 3], // 1 IN, 0 ON, 3 OFF
    [0, 3, 1], // 0 IN, 3 ON, 1 OFF
  ],
  4: [
    [1, 1, 2], // 1 IN, 1 ON, 2 OFF
    [0, 4, 0], // 0 IN, 4 ON, 0 OFF
  ],
  6: [
    [2, 0, 2], // 2 IN, 0 ON, 2 OFF
    [1, 3, 0], // 1 IN, 3 ON, 0 OFF
  ],
};

/** Labels displayed in the clarification UI. */
const AMBIGUOUS_COMBO_LABELS: Record<number, string[]> = {
  3: ['1 IN', '3 ON'],
  4: ['1 IN + 1 ON', '4 ON'],
  6: ['2 IN', '1 IN + 3 ON'],
};

/** Row 1 of the score grid: 0–5 */
const SCORE_ROW_1 = [0, 1, 2, 3, 4, 5] as const;
/** Row 2 of the score grid: 6–12 (no 11 — impossible in cornhole) */
const SCORE_ROW_2 = [6, 7, 8, 9, 10, 12] as const;

/**
 * For unambiguous scores, returns the unique [bagsIn, bagsOn, bagsOff] breakdown.
 * Score 11 is not in the grid (impossible). Score 0 = 4 bags off.
 */
export function decomposeScore(score: number): [number, number, number] | null {
  if (score in AMBIGUOUS_COMBOS) return null; // ambiguous — caller must handle
  switch (score) {
    case 0:  return [0, 0, 4];
    case 1:  return [0, 1, 3];
    case 2:  return [0, 2, 2];
    case 5:  return [1, 2, 1];
    case 7:  return [2, 1, 1];
    case 8:  return [2, 2, 0];
    case 9:  return [3, 0, 1];
    case 10: return [3, 1, 0];
    case 12: return [4, 0, 0];
    default: return null;
  }
}

export function isAmbiguousScore(score: number): boolean {
  return score in AMBIGUOUS_COMBOS;
}

// ============================================================
// Types
// ============================================================

export interface ScoreEntryAdvancedProps {
  /** Players who threw this round. Index 0 = left column, index 1 = right column. */
  players: Array<{ id: string; name: string; teamColor: string }>;
  roundNumber: number;
  /** Called with per-player stats when the user confirms the round. */
  onSubmit: (playerStats: PlayerStats[]) => void;
  /** Called when the overlay is dismissed without submitting. */
  onDismiss: () => void;
  /** Optional gear button callback — renders gear icon to the left of Cancel when provided */
  onGearTap?: () => void;
  /** Label for the submit button. Defaults to "Submit Round". Pass "Update Round" in correction mode. */
  submitLabel?: string;
}

interface PlayerEntryState {
  score: number | null;
  /** Index into AMBIGUOUS_COMBOS[score] — null until clarified */
  comboIndex: number | null;
}

// ============================================================
// ScoreEntryAdvanced
// ============================================================

/**
 * Score entry overlay — Advanced PPR mode.
 * Renders a 6×2 score grid per player (side-by-side columns) with an inline
 * clarification row for ambiguous scores (3, 4, 6). Dismissed via Cancel or
 * Submit only.
 *
 * Layout scaling: card is set to 80dvh × 90vw with container-type: size.
 * All internal sizing uses cqh/cqw units relative to the card container so
 * the UI scales proportionally on any tablet — 7" through iPad Pro.
 * Matches ScoreEntryIntermediate exactly: same card dimensions, same player
 * name headers (10cqh / 8cqh font), same floating round badge, same action
 * buttons.
 *
 * Vertical budget (80dvh = 100cqh of this container):
 *   Padding top+bottom:    4cqh
 *   Players area (flex:1): ~87cqh
 *     ↳ Player name header:  10cqh
 *     ↳ Score grid rows:     flex:1 (~76cqh → ~37.5cqh each row)
 *     ↳ Clarification row:   ~14cqh (conditional — shrinks score grid)
 *   Action buttons:        ~7cqh
 *   Gap:                   ~2cqh
 *   Total ≈ 100cqh — round badge floats over player name area, no row cost
 */
export function ScoreEntryAdvanced({
  players,
  roundNumber,
  onSubmit,
  onDismiss,
  onGearTap,
  submitLabel = 'Submit Round',
}: ScoreEntryAdvancedProps) {
  const brand = useBrand();
  const [entries, setEntries] = useState<PlayerEntryState[]>(
    players.map(() => ({ score: null, comboIndex: null })),
  );

  const handleScoreTap = (playerIdx: number, score: number) => {
    setEntries((prev) => {
      const next = [...prev];
      next[playerIdx] = {
        score,
        // Clear prior clarification choice if score changed; unambiguous scores get comboIndex 0
        comboIndex: isAmbiguousScore(score) ? null : 0,
      };
      return next;
    });
  };

  const handleComboTap = (playerIdx: number, comboIndex: number) => {
    setEntries((prev) => {
      const next = [...prev];
      next[playerIdx] = { ...next[playerIdx], comboIndex };
      return next;
    });
  };

  /** Both players have scores, and any ambiguous scores have been clarified. */
  const isReadyToSubmit = entries.every((e) => {
    if (e.score === null) return false;
    if (isAmbiguousScore(e.score)) return e.comboIndex !== null;
    return true;
  });

  const handleSubmit = () => {
    if (!isReadyToSubmit) return;

    const stats: PlayerStats[] = entries.map((e, i) => {
      const score = e.score!;
      let bagsIn: number;
      let bagsOn: number;
      let bagsOff: number;

      if (isAmbiguousScore(score)) {
        [bagsIn, bagsOn, bagsOff] = AMBIGUOUS_COMBOS[score][e.comboIndex!];
      } else {
        [bagsIn, bagsOn, bagsOff] = decomposeScore(score)!;
      }

      return {
        playerId: players[i].id,
        bagsIn,
        bagsOn,
        bagsOff,
        totalPoints: score,
      };
    });

    onSubmit(stats);
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.80)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  };

  /**
   * Card is the CSS size-query container.
   * Height: 80dvh (falls back to 80vh for older browsers).
   * Supported in Chrome 108+, Firefox 110+, Safari 15.5+ — all modern tablets.
   * Width: 90vw — fills 90% of viewport; no pixel cap so landscape tablets use full space.
   * All cqh/cqw values below are relative to THIS element's dimensions.
   */
  const cardStyle: React.CSSProperties = {
    backgroundColor: brand.colors.background,
    borderRadius: '1.5cqh',
    height: '80dvh',
    width: '90vw',
    padding: '2cqh 1.5cqw',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5cqh',
    boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
    color: brand.colors.text,
    fontFamily: 'system-ui, sans-serif',
    // Makes this element a CSS container for cqh/cqw queries
    containerType: 'size',
    overflow: 'hidden',
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Enter round score">
      <div style={cardStyle}>
        {/* Player columns — flex:1 fills full height; position:relative anchors the floating round badge */}
        <div style={{ display: 'flex', gap: '1.5cqw', flex: 1, minHeight: 0, position: 'relative' }}>
          {/* Floating round badge — centered over player name headers, no layout row cost */}
          <div
            style={{
              position: 'absolute',
              top: '1.5cqh',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#000000',
              color: '#ffffff',
              borderRadius: '2cqh',
              padding: '0.4cqh 1.2cqw',
              pointerEvents: 'none',
              zIndex: 10,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: '1.8cqh', fontWeight: 500, letterSpacing: '0.08em', opacity: 0.85, lineHeight: 1.2, textTransform: 'uppercase' }}>
              ROUND
            </div>
            <div style={{ fontSize: '4.5cqh', fontWeight: 900, lineHeight: 1 }}>
              {roundNumber}
            </div>
          </div>

          {players.map((player, idx) => (
            <PlayerScoreColumn
              key={player.id}
              player={player}
              entry={entries[idx]}
              onScoreTap={(score) => handleScoreTap(idx, score)}
              onComboTap={(comboIdx) => handleComboTap(idx, comboIdx)}
              textColor={brand.colors.text}
            />
          ))}
        </div>

        {/* Action buttons — exact same styling as ScoreEntryIntermediate */}
        <div style={{ display: 'flex', gap: '1cqw', flexShrink: 0 }}>
          {onGearTap && (
            <button
              onClick={onGearTap}
              aria-label="Game settings"
              style={{
                flexShrink: 0,
                width: '8cqh',
                height: '8cqh',
                padding: 0,
                fontSize: '3.5cqh',
                border: `2px solid ${brand.colors.text}`,
                borderRadius: '1cqh',
                background: 'transparent',
                color: brand.colors.text,
                cursor: 'pointer',
                touchAction: 'manipulation',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ⚙️
            </button>
          )}
          <button
            onClick={onDismiss}
            style={{
              flex: 1,
              padding: '2.5cqh 0',
              fontSize: '2.5cqh',
              border: `2px solid ${brand.colors.text}`,
              borderRadius: '1cqh',
              background: 'transparent',
              color: brand.colors.text,
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isReadyToSubmit}
            style={{
              flex: 2,
              padding: '2.5cqh 0',
              fontSize: '3cqh',
              fontWeight: 700,
              border: 'none',
              borderRadius: '1cqh',
              background: isReadyToSubmit ? brand.colors.primary : 'rgba(255,255,255,0.15)',
              color: isReadyToSubmit ? '#ffffff' : 'rgba(255,255,255,0.4)',
              cursor: isReadyToSubmit ? 'pointer' : 'not-allowed',
              touchAction: 'manipulation',
              transition: 'background 0.15s',
            }}
            aria-disabled={!isReadyToSubmit}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PlayerScoreColumn — internal
// ============================================================

interface PlayerScoreColumnProps {
  player: { id: string; name: string; teamColor: string };
  entry: PlayerEntryState;
  onScoreTap: (score: number) => void;
  onComboTap: (comboIdx: number) => void;
  textColor: string;
}

function PlayerScoreColumn({
  player,
  entry,
  onScoreTap,
  onComboTap,
  textColor,
}: PlayerScoreColumnProps) {
  const needsClarification = entry.score !== null && isAmbiguousScore(entry.score);

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '1cqh',
        minHeight: 0,
        // container-type:inline-size on this column lets ScoreRow use cqw relative to THIS column
        containerType: 'inline-size',
      }}
    >
      {/* Player name header — matches Intermediate exactly: 10cqh tall, 8cqh font = 80% fill */}
      <div
        style={{
          height: '10cqh',
          fontSize: '8cqh',
          fontWeight: 700,
          textAlign: 'center',
          borderRadius: '0.8cqh',
          background: player.teamColor,
          color: '#ffffff',
          flexShrink: 0,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {player.name}
      </div>

      {/* Score grid — two rows of 6 square buttons.
          Each button is a square: width = (100cqw - 5 gaps) / 6 per row.
          We use aspect-ratio:1 and let cqw drive size so buttons are always square. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1cqh',
          flexShrink: 0,
        }}
        role="group"
        aria-label={`Score grid for ${player.name}`}
      >
        <ScoreRow
          scores={SCORE_ROW_1}
          selectedScore={entry.score}
          teamColor={player.teamColor}
          textColor={textColor}
          onTap={onScoreTap}
        />
        <ScoreRow
          scores={SCORE_ROW_2}
          selectedScore={entry.score}
          teamColor={player.teamColor}
          textColor={textColor}
          onTap={onScoreTap}
        />
      </div>

      {/* Clarification row — space is ALWAYS reserved to prevent layout shifts.
          Buttons are visible only when an ambiguous score needs clarification. */}
      <ClarificationRow
        score={entry.score}
        selectedComboIdx={entry.comboIndex}
        teamColor={player.teamColor}
        textColor={textColor}
        visible={needsClarification}
        onSelect={onComboTap}
      />
    </div>
  );
}

// ============================================================
// ScoreRow — internal
// ============================================================

interface ScoreRowProps {
  scores: ReadonlyArray<number>;
  selectedScore: number | null;
  teamColor: string;
  textColor: string;
  onTap: (score: number) => void;
}

/**
 * One horizontal row of 6 square score buttons.
 * Sizing: each button = (100cqw - 5×gap) / 6 wide, with aspect-ratio:1 to enforce square.
 * cqw here is relative to the PlayerScoreColumn container (containerType: inline-size).
 * Font = 80% of button width = 0.8 × buttonWidth. With 6 buttons + 5×1cqw gaps:
 *   buttonWidth ≈ (100cqw - 5cqw) / 6 = 15.83cqw
 *   font ≈ 0.8 × 15.83cqw ≈ 12.67cqw → rounded to 12.5cqw
 */
function ScoreRow({ scores, selectedScore, teamColor, textColor, onTap }: ScoreRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${scores.length}, 1fr)`,
        gap: '1cqw',
      }}
    >
      {scores.map((score) => {
        const isSelected = selectedScore === score;
        return (
          <button
            key={score}
            onClick={() => onTap(score)}
            style={{
              // Square: width is driven by grid, height matches via aspect-ratio
              width: '100%',
              aspectRatio: '1 / 1',
              // Font = 80% of button width. buttonWidth ≈ (100cqw - 5×1cqw) / 6 ≈ 15.83cqw
              fontSize: '12.5cqw',
              lineHeight: 1,
              fontWeight: 700,
              border: isSelected ? `3px solid ${teamColor}` : '2px solid rgba(255,255,255,0.2)',
              borderRadius: '1.2cqw',
              background: isSelected ? teamColor : 'rgba(255,255,255,0.08)',
              color: isSelected ? '#ffffff' : textColor,
              cursor: 'pointer',
              touchAction: 'manipulation',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.1s, border-color 0.1s',
              padding: 0,
              opacity: isSelected ? 1 : 0.75,
            }}
            aria-pressed={isSelected}
            aria-label={`Score ${score}`}
          >
            {score}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// ClarificationRow — internal
// ============================================================

interface ClarificationRowProps {
  score: number | null;
  selectedComboIdx: number | null;
  teamColor: string;
  textColor: string;
  /** Controls visibility — space is always reserved to prevent layout shifts. */
  visible: boolean;
  onSelect: (comboIdx: number) => void;
}

/**
 * Third row — space is ALWAYS present in the layout to prevent height shifts.
 * When visible=false, the row is invisible (opacity 0, pointer-events none) but
 * occupies its full height. When visible=true (score 3, 4, or 6), the two combo
 * buttons appear in the pre-reserved space.
 *
 * No question prompt — advanced players know what the combos mean.
 * Button size: cqw-relative to match ScoreRow square sizing.
 */
function ClarificationRow({
  score,
  selectedComboIdx,
  teamColor,
  textColor,
  visible,
  onSelect,
}: ClarificationRowProps) {
  const labels = score !== null ? AMBIGUOUS_COMBO_LABELS[score] : null;

  return (
    <div
      style={{
        flexShrink: 0,
        // Two combo buttons with same aspect-ratio:1 as score buttons, using 1fr 1fr grid
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1cqw',
        // 5% vertical spacing above the clarification row (from score grid rows)
        marginTop: '5cqh',
        // Hidden when not needed — but space is always reserved
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.15s',
      }}
      role="group"
      aria-label={score !== null ? `Clarify ${score} point combo` : undefined}
      aria-hidden={!visible}
    >
      {[0, 1].map((idx) => {
        const label = labels?.[idx] ?? '';
        const isSelected = selectedComboIdx === idx;
        return (
          <button
            key={idx}
            onClick={() => onSelect(idx)}
            style={{
              // Rectangular buttons — wide and short (no aspect-ratio constraint)
              width: '100%',
              fontSize: '8cqw',
              fontWeight: 500,
              lineHeight: 1.1,
              border: isSelected ? `3px solid ${teamColor}` : '2px solid rgba(255,255,255,0.2)',
              borderRadius: '1.2cqw',
              background: isSelected ? teamColor : 'rgba(255,255,255,0.08)',
              color: isSelected ? '#ffffff' : textColor,
              cursor: 'pointer',
              touchAction: 'manipulation',
              transition: 'background 0.1s, border-color 0.1s',
              opacity: isSelected ? 1 : 0.75,
              textAlign: 'center',
              padding: '0.5cqw',
            }}
            aria-pressed={isSelected}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
